import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import clientlogo from "../assets/images/clientlogo.png";
import aittClientlogo from "../assets/images/aitt_student_portal_logo.jpg";
import * as constants from "../constants";
import ErrorSound from "../assets/sounds/error.mp3";
import { toast } from "react-toastify";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { IoArrowBack } from "react-icons/io5";
import axios from "axios";
import { sendOtp } from "../services/helperFunction";

const OTPVerificationPage = () => {
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [errorAudio] = useState(new Audio(ErrorSound));

  const inputRefs = [useRef(), useRef(), useRef(), useRef()];
  const navigate = useNavigate();

  useEffect(()=>{
    const otpValue = otp.join("");

    if(otpValue.length === 4 && !loading) {
      verifyOTP();
    }
  },[otp])

  // Timer for resend OTP
  useEffect(() => {
    let interval;
    if (resendTimer > 0 && !canResend) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [resendTimer, canResend]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs[0].current?.focus();
  }, []);

  const playSound = (audioFile) => {
    audioFile.play();
  };

  const handleChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Take only the last character
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        inputRefs[index - 1].current?.focus();
      }
    }
    // Handle arrow keys
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
    if (e.key === "ArrowRight" && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 4);
    
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    pastedData.split("").forEach((char, index) => {
      if (index < 4) {
        newOtp[index] = char;
      }
    });
    setOtp(newOtp);

    // Focus the next empty input or the last one
    const nextEmptyIndex = newOtp.findIndex((val) => val === "");
    if (nextEmptyIndex !== -1) {
      inputRefs[nextEmptyIndex].current?.focus();
    } else {
      inputRefs[3].current?.focus();
    }
  };
  
  const studentId = localStorage.getItem("studentId");

    useEffect(() => {
    // If there is no studentId in localStorage, user has not logged in properly
    if (!studentId) {
      navigate("/login", { replace: true });
      return;
    }

    // If OTP already verified, don't allow staying on OTP page
    const otpVerified = localStorage.getItem("otpVerified");
    if (otpVerified === "true") {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate, studentId]);

  const verifyOTP = async () => {
  
    const otpValue = otp.join("");

    if (otpValue.length !== 4) {
      toast.error("Please enter complete OTP", { toastId: "otperror" });
      playSound(errorAudio);
      return;
    }

    setLoading(true);

     try {
        const response = await axios.post(`${constants.BASEURL}Login/verifyOtp`, {
          otp: otpValue,
          studentId,
        });

        if (response.data.success) {
          toast.success("OTP Verified Successfully!");
          localStorage.setItem("otpVerified", "true");
          navigate("/dashboard");
        } else {
          playSound(errorAudio);
          toast.error("Invalid OTP", { toastId: "otperror" });
          setOtp(["", "", "", ""]);
          inputRefs[0].current?.focus();
        }
      } catch (error) {
        console.log(error, "catch error");
        toast.error("Verification failed", { toastId: "otperror" });
        playSound(errorAudio);
      } finally {
        setLoading(false);
      }
  };

  const resendOTP = async () => {
    if (!canResend) return;
    await sendOtp(studentId)
  };

  const goBack = () => {
    navigate(-1);
  };

    const CLIENT_LOGOS = {
    AITT: aittClientlogo,
    default: clientlogo,
  };

  // Get the appropriate logo based on config
  const getClientLogo = () => {
    const clientName = window.configs?.showCustomLoginClientName;
    return CLIENT_LOGOS[clientName] || CLIENT_LOGOS.default;
  };

  return (
    <>
      <div className="min-h-screen bg-white flex">
        <div className="flex-1 flex flex-col justify-center py-12 px-6 sm:px-6 lg:block lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm">
            {/* Back Button */}
            <button
              onClick={goBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
            >
              <IoArrowBack className="w-5 h-5" />
              <span>Back</span>
            </button>

            <div>
              {window.configs?.showClientLogoLoginPage && (
                // <img className="h-18 w-auto" src={clientlogo} alt="Logo" />
                <img className="h-18 w-auto" src={getClientLogo()} alt="Logo" />
              )}
            </div>

            <div>
              <h2 className="mt-6 text-3xl leading-9 font-extrabold">
                Verify OTP
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                We've sent a 4-digit verification code to your registered
                email
              </p>
            </div>

            <div className="mt-8">
              <form onSubmit={(e)=>{
                e.preventDefault();
                verifyOTP();
              }}>
                {/* OTP Input Fields */}
                <div>
                  <label className="block font-semibold text-[#374151] text-[0.875rem] mb-4">
                    Enter OTP
                  </label>
                  <div className="flex gap-3 justify-center sm:justify-start">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={inputRefs[index]}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={handlePaste}
                        className="w-14 h-14 text-center text-2xl font-bold 
                          border-2 rounded-lg focus:outline-none focus:border-blue-600 
                          transition-colors text-black
                          hover:border-gray-400"
                        disabled={loading}
                      />
                    ))}
                  </div>
                </div>

                {/* Resend OTP */}
                <div className="mt-6 text-center sm:text-left">
                  {canResend ? (
                    <button
                      type="button"
                      onClick={resendOTP}
                      disabled={loading}
                      className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                    >
                      Resend OTP
                    </button>
                  ) : (
                    <p className="text-sm text-gray-500">
                      Resend OTP in{" "}
                      <span className="font-semibold text-blue-600">
                        {resendTimer}s
                      </span>
                    </p>
                  )}
                </div>

                {/* Verify Button */}
                <div className="mt-6">
                  <span className="block w-full rounded-md shadow-[0 0 0.125rem 0 rgba(0, 0, 0, 0.1)]">
                    <button
                      type="submit"
                      disabled={loading || otp.join("").length !== 4}
                      className="w-full flex gap-x-2 justify-center items-center 
                        text-white bg-blue-700 hover:bg-blue-800 
                        focus:ring-4 focus:ring-blue-300 font-medium 
                        rounded-lg text-sm px-5 py-2.5 me-2 mb-2 
                        dark:bg-blue-600 dark:hover:bg-blue-700 
                        focus:outline-none dark:focus:ring-blue-800
                        disabled:opacity-50 disabled:cursor-not-allowed
                        transition-all"
                    >
                      {loading && (
                        <AiOutlineLoading3Quarters className="w-4 h-4 fill-white animate-spin" />
                      )}
                      Verify OTP
                    </button>
                  </span>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Right Side Image */}
        {
          window.configs.showCustomLoginClientName ? (<div className="hidden lg:block relative w-0 flex-1 bg-[#2662C5] text-white">
            <section>
              <svg class="inline-block fill-current w-full h-auto text-blue-200/40" viewBox="0 0 1440 450" xmlns="http://www.w3.org/2000/svg">
                <path d="M1189.2 169.2H421H253.8C159.8 169.2 69.1 203.1 0 262.6V449.8C30.5 349.9 131.3 276.7 252 276.7H424.1H1187.4C1280.9 276.7 1371 243.2 1440 184.3V0C1408.1 97.9 1308.3 169.2 1189.2 169.2Z"></path>
              </svg>

              <div className="h-full flex flex-col justify-center px-8 leading-8 mt-10">
                  <h1 className="text-2xl mb-6 font-semibold">Welcome to AITT Student Resource Centre</h1>
                  <p className="text-lg font-medium mb-2">Student Learning Management System </p>

                  <ul className="ml-6">
                    <li className="list-disc">Check Invoices and Payments details online</li>
                    <li className="list-disc">Update your Personal details</li>
                    <li className="list-disc">Check your Enrolments details</li>
                    <li className="list-disc">Check Current and Historical Academic details</li>
                    <li className="list-disc">Student Request Form</li>
                  </ul>
              </div>
            </section>
          </div>) : (<div className="hidden lg:block relative w-0 flex-1">
          <img
            className="absolute inset-0 h-full w-full object-cover"
            src="https://images.unsplash.com/photo-1505904267569-f02eaeb45a4c?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1908&q=80"
            alt="Verification"
          />
        </div>)
        }
        
        
      </div>
    </>
  );
};

export default OTPVerificationPage;